import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faArrowLeft,
  faPlay,
  faLock,
  faLockOpen,
  faUserPlus,
  faTrash,
  faPaperPlane,
  faClockRotateLeft,
  faCommentDots,
  faUsers,
  faLayerGroup,
  faCircleCheck,
  faMagnifyingGlass,
  faXmark,
  faEye,
  faFileArrowUp
} from '@fortawesome/free-solid-svg-icons';

import { projectsApi, usersApi, assessmentApi } from '../services/api';
import { Button, Badge, Spinner, Input } from '../components/ui';
import { useProjectActivity } from '../hooks/useProjectActivity';
import { useAssessmentJob } from '../hooks/useAssessmentJob';

/*
|--------------------------------------------------------------------------
| Start Assessment (from a Project)
|--------------------------------------------------------------------------
| Reuses the same POST /assessment/start contract the Assess page's own
| scope picker (AssessmentScopeModal) uses — { projectName, appIds,
| pageIds } — but builds the scope from the project's own resources
| instead of asking the user to pick apps/pages again. projectId is also
| sent so the backend can link the resulting assessment run back to this
| project if it wants to (not required by the existing contract, additive
| only).
|--------------------------------------------------------------------------
*/

function buildAssessmentScopeFromProject(project) {

  const resources = project?.resources || [];

  const appIds = resources
    .filter(r => String(r.type).toLowerCase() === 'app')
    .map(r => r.id);

  const pageIds = resources
    .filter(r => String(r.type).toLowerCase() !== 'app')
    .map(r => r.id);

  // Every resource's source is either "file" (added via offline JSON
  // upload) or a live Domo-connected resource. If every resource in this
  // project is offline, the assessment should run against
  // /assessment/offline-start instead of the normal live /assessment/start
  // — a mixed set, or an all-Domo set, still uses the normal endpoint.
  const isOffline = resources.length > 0 && resources.every(r => r.source === 'file');

  return {
    projectId: project?.id,
    projectName: project?.name || 'Untitled Project',
    appIds,
    pageIds,
    isOffline
  };

}

/*
|--------------------------------------------------------------------------
| Project Detail Page
|--------------------------------------------------------------------------
| Everything about a single project: the apps/pages assigned to it for
| assessment, the users working on it, a comment thread, and the
| lock/unlock control. When a project is locked, every edit affordance on
| this page (add/remove resources, assign/unassign users, edit details) is
| disabled — the project becomes read-only until it's unlocked again.
|--------------------------------------------------------------------------
*/

export default function ProjectDetailPage() {

  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showAddResourcesModal, setShowAddResourcesModal] = useState(false);
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sidePanel, setSidePanel] = useState('activity');

  // Offline file upload — opens a modal where the user attaches one or
  // more JSON files, sees them listed, and tags each one with what kind
  // of data it contains (App / Page / Card / Dataset / Beast Mode /
  // Datasource) before sending them as-is (unparsed) to the backend.
  const [showUploadModal, setShowUploadModal] = useState(false);

  const { data, isLoading, isError, error } = useQuery(
    ['project', id],
    () => projectsApi.get(id),
    { refetchOnWindowFocus: false }
  );

  const project = data?.data?.data || data?.data || null;
  const locked = !!project?.locked;

  const invalidate = () => queryClient.invalidateQueries(['project', id]);

  // Live activity log for this project — seeded from the activity array
  // GET /projects/:id already returns, then kept up to date in real time
  // over the same WebSocket infra used for job progress elsewhere in the
  // app.
  const { activity, isConnected } = useProjectActivity(id, project?.activity);

  // Past assessment runs for this project — GET /projects/:id already
  // returns its own assessmentHistory array, so no separate request is
  // needed here.
  const projectAssessmentHistory = project?.assessmentHistory || [];

  // The id of the most recent assessment run tied to this project — used
  // to power the "View Assessment" button. Seeded from the project's own
  // history (most recent entry) on load, then overwritten with the fresh
  // id the moment a new run is started from this page.
  const [latestAssessId, setLatestAssessId] = useState(null);

  useEffect(() => {
    if (latestAssessId || !projectAssessmentHistory.length) return;

    const sorted = [...projectAssessmentHistory].sort((a, b) => {
      const aTime = new Date(a.completedAt || a.createdAt || a.startedAt || 0).getTime();
      const bTime = new Date(b.completedAt || b.createdAt || b.startedAt || 0).getTime();
      return bTime - aTime;
    });

    const mostRecent = sorted[0];
    const mostRecentId = mostRecent?.id ?? mostRecent?.jobId ?? mostRecent?._id;

    if (mostRecentId != null) {
      setLatestAssessId(mostRecentId);
    }
  }, [projectAssessmentHistory.length]);

  // Live status/progress for whatever assessment job is currently running.
  // This is the same global polling + WebSocket infra the Assess page
  // itself uses (/assessment/status), so starting a run from here shows
  // its progress right on this page too, without needing to navigate away.
  const {
    status: jobStatus,
    progress: jobProgress,
    isRunning: assessmentIsRunning,
    startAssessment
  } = useAssessmentJob(undefined, `project:${id}`, { projectId: id });

  const lastSuccess = jobStatus?.lastSuccess;

  const [startingAssessment, setStartingAssessment] = useState(false);

  const handleStartAssessment = async () => {

    const resourceCount = project?.resources?.length || 0;

    if (!resourceCount) {
      toast.error('Add at least one app or page to this project before starting an assessment.');
      return;
    }

    setStartingAssessment(true);

    try {
      const { isOffline, ...scope } = buildAssessmentScopeFromProject(project);
      const data = await startAssessment(scope, { offline: isOffline });

      // Keep the "View Assessment" button pointed at this new run — stay
      // on the project page so its progress bar (below) can be watched
      // live instead of forcing a redirect to /assess/:id.
      const newAssessId =
        data?.job?.jobId ||
        data?.data?.jobId ||
        data?.jobId ||
        data?.data?.id ||
        data?.id;

      if (newAssessId) {
        setLatestAssessId(newAssessId);
      }

    } catch (err) {
      // startAssessment already toasts on failure.
    } finally {
      setStartingAssessment(false);
    }

  };

  const lockMutation = useMutation(
    () => (locked ? projectsApi.unlock(id) : projectsApi.lock(id)),
    {
      onSuccess: () => {
        toast.success(locked ? 'Project unlocked' : 'Project locked');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to update lock state')
    }
  );

  const deleteMutation = useMutation(
    () => projectsApi.remove(id),
    {
      onSuccess: () => {
        toast.success('Project deleted');
        queryClient.invalidateQueries('projects');
        navigate('/projects');
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to delete project')
    }
  );

  const removeResourceMutation = useMutation(
    (resourceId) => projectsApi.removeResource(id, resourceId),
    {
      onSuccess: () => { toast.success('Removed'); invalidate(); },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to remove')
    }
  );

  const unassignUserMutation = useMutation(
    (userId) => projectsApi.unassignUser(id, userId),
    {
      onSuccess: () => { toast.success('User removed from project'); invalidate(); },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to remove user')
    }
  );

  const addCommentMutation = useMutation(
    (body) => projectsApi.addComment(id, body),
    {
      onSuccess: () => { setCommentText(''); invalidate(); },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to add comment')
    }
  );

  if (isLoading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            background: 'var(--bg-card)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 28,
            maxWidth: 420,
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 10 }}>⚠️</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Couldn't load this project</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 18 }}>
            {error?.response?.data?.error || error?.message || 'Not found'}
          </div>
          <Button variant="secondary" onClick={() => navigate('/projects')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const resources = project.resources || [];
  const users = project.users || [];
  const comments = project.comments || [];

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-page)' }}>

      {/* ================= HERO HEADER ================= */}
      <div
        style={{
          background: 'var(--accent)',
          padding: '28px 32px',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 85% 0%, rgba(255,255,255,0.14), transparent 55%)',
            pointerEvents: 'none'
          }}
        />

        <button
          onClick={() => navigate('/projects')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(255,255,255,0.14)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: 'rgba(255,255,255,0.9)',
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            marginBottom: 18,
            transition: 'background .15s'
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> All Projects
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 0 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-lg)',
                background: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 20
              }}
            >
              <FontAwesomeIcon icon={faFolder} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#fff' }}>
                  {project.name}
                </h1>
                {locked ? (
                  <span style={hero_badge('rgba(245,158,11,0.25)', '#FEF3C7')}>
                    <FontAwesomeIcon icon={faLock} /> Locked
                  </span>
                ) : (
                  <span style={hero_badge('rgba(16,185,129,0.25)', '#D1FAE5')}>
                    <FontAwesomeIcon icon={faCircleCheck} /> Active
                  </span>
                )}
              </div>
              <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: 13, maxWidth: 560 }}>
                {project.description || 'No description provided.'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <HeroButton
              onClick={handleStartAssessment}
              loading={startingAssessment}
              disabled={!resources.length}
              title={!resources.length ? 'Add at least one app or page first' : 'Run an assessment on this project\'s apps/pages'}
              primary
            >
              <FontAwesomeIcon icon={faPlay} /> Start Assessment
            </HeroButton>
            <HeroButton
              onClick={() => lockMutation.mutate()}
              loading={lockMutation.isLoading}
            >
              <FontAwesomeIcon icon={locked ? faLockOpen : faLock} /> {locked ? 'Unlock' : 'Lock'}
            </HeroButton>
            <HeroButton
              onClick={() => setShowDeleteModal(true)}
              disabled={locked}
              title={locked ? 'Unlock this project before deleting it' : 'Delete this project'}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </HeroButton>
          </div>

        </div>

        {/* STAT CHIPS */}
        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <StatChip icon={faLayerGroup} label="Apps & Reports" value={resources.length} />
          <StatChip icon={faUsers} label="Assigned Users" value={users.length} />
          <StatChip icon={faCommentDots} label="Comments" value={comments.length} />
          <StatChip icon={faClockRotateLeft} label="Assessment Runs" value={projectAssessmentHistory.length} />
        </div>

        {/* ASSESSMENT STATUS: live progress while running, last sync time
            once complete, and a link to the latest run's detail page. */}
        {(assessmentIsRunning || lastSuccess || latestAssessId) && (
          <div
            style={{
              marginTop: 16,
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap'
            }}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              {assessmentIsRunning ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, fontWeight: 600 }}>
                    <span>{jobProgress?.stage ? jobProgress.stage : 'Starting assessment...'}</span>
                    <span>{jobProgress?.percent || 0}%</span>
                  </div>
                  <div style={{ width: '100%', height: 7, background: 'rgba(255,255,255,0.22)', borderRadius: 4, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${jobProgress?.percent || 0}%`,
                        height: '100%',
                        background: '#fff',
                        borderRadius: 4,
                        transition: 'width 0.4s ease'
                      }}
                    />
                  </div>
                </div>
              ) : lastSuccess ? (
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.9)' }}>
                  <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: 6 }} />
                  Last sync: {formatRelativeTime(lastSuccess.completedAt || lastSuccess.snapshot?.createdAt)}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)' }}>
                  No assessment run yet for this project.
                </div>
              )}
            </div>

            {latestAssessId != null && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/assess/${latestAssessId}`)}
                style={{ background: 'rgba(255,255,255,0.16)', color: '#fff', border: 'none', flexShrink: 0 }}
              >
                <FontAwesomeIcon icon={faEye} /> View Assessment
              </Button>
            )}
          </div>
        )}

      </div>

      {locked && (
        <div
          style={{
            margin: '18px 32px 0',
            padding: '11px 16px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--warning-bg)',
            color: 'var(--warning-text)',
            fontSize: 12.5,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <FontAwesomeIcon icon={faLock} />
          This project is locked. Resources and assigned users cannot be changed until it's unlocked.
        </div>
      )}

      {/* ================= BODY ================= */}
      <div
        style={{
          padding: 32,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
          gap: 20,
          alignItems: 'flex-start'
        }}
      >

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* RESOURCES */}
          <SectionCard
            icon={faLayerGroup}
            iconColor="var(--accent)"
            title="Apps & Reports"
            count={resources.length}
            action={
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={locked}
                  onClick={() => setShowUploadModal(true)}
                  title="Upload one or more JSON files"
                >
                  <FontAwesomeIcon icon={faFileArrowUp} /> Offline Files
                </Button>
                <Button variant="secondary" size="sm" disabled={locked} onClick={() => setShowAddResourcesModal(true)}>
                  Connect to DOMO
                </Button>
              </div>
            }
          >
            {!resources.length ? (
              <EmptyRow text="No apps or reports added yet. Add the Domo apps/pages this project should assess." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {resources.map(resource => {

                  const resourceName =
                    resource.name ||
                    resource.title ||
                    resource.label ||
                    resource.pageName ||
                    resource.appName ||
                    `Resource ${resource.id ?? ''}`;

                  return (
                    <ListRow key={resource.id}>
                      <Badge variant="accent">{resource.type || 'Page'}</Badge>
                      <Badge variant="accent">{resource?.source === "file" ? "Offline" : "Domo"}</Badge>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {resourceName}
                      </span>
                      {!locked && (
                        <RowRemoveButton
                          onClick={() => removeResourceMutation.mutate(resource.id)}
                          label={`Remove ${resourceName}`}
                        />
                      )}
                    </ListRow>
                  );

                })}
              </div>
            )}
          </SectionCard>

          {/* COMMENTS */}
          <SectionCard icon={faCommentDots} iconColor="var(--accent)" title="Comments" count={comments.length}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {!comments.length ? (
                <EmptyRow text="No comments yet." />
              ) : (
                comments.map(comment => (
                  <div
                    key={comment.id}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--bg-secondary)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {comment.userName || comment.user?.name || 'Unknown'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                        {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                      {comment.text}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder={locked ? 'Comments are read-only while the project is locked' : 'Add a comment...'}
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  disabled={locked}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && commentText.trim() && !locked) {
                      addCommentMutation.mutate({ text: commentText.trim() });
                    }
                  }}
                />
              </div>
              <Button
                variant="primary"
                disabled={locked || !commentText.trim()}
                loading={addCommentMutation.isLoading}
                onClick={() => addCommentMutation.mutate({ text: commentText.trim() })}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </Button>
            </div>
          </SectionCard>

        </div>

        {/* ================= SIDE PANEL (tabbed) ================= */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)',
            overflow: 'hidden',
            position: 'sticky',
            top: 20
          }}
        >

          <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)' }}>
            <SidePanelTab
              icon={faLayerGroup}
              label="Activity"
              count={activity.length}
              active={sidePanel === 'activity'}
              onClick={() => setSidePanel('activity')}
              indicator={isConnected}
            />
            <SidePanelTab
              icon={faClockRotateLeft}
              label="History"
              count={projectAssessmentHistory.length}
              active={sidePanel === 'history'}
              onClick={() => setSidePanel('history')}
            />
            <SidePanelTab
              icon={faUsers}
              label="Users"
              count={users.length}
              active={sidePanel === 'users'}
              onClick={() => setSidePanel('users')}
            />
          </div>

          <div style={{ padding: 16 }}>

            {sidePanel === 'activity' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 11, color: isConnected ? 'var(--success)' : 'var(--text-tertiary)' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: isConnected ? 'var(--success)' : 'var(--text-tertiary)', display: 'inline-block' }} />
                  {isConnected ? 'Live' : 'Connecting...'}
                </div>

                {!activity.length ? (
                  <EmptyRow text="No activity yet. Actions taken on this project will appear here in real time." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
                    {activity.map(entry => (
                      <ActivityLogEntry key={entry.id} entry={entry} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {sidePanel === 'history' && (
              <div>
                {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  {resources.length > 0 && (
                    <Button variant="secondary" size="sm" loading={startingAssessment} onClick={handleStartAssessment}>
                      <FontAwesomeIcon icon={faPlay} /> Run
                    </Button>
                  )}
                </div> */}

                {!projectAssessmentHistory.length ? (
                  <EmptyRow text="No assessments run for this project yet." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {projectAssessmentHistory.map((entry, index) => (
                      <ProjectHistoryEntry
                        key={entry.id ?? entry.jobId ?? index}
                        entry={entry}
                        onView={() => {
                          const entryId = entry.id ?? entry.jobId ?? entry._id;
                          if (entryId != null) navigate(`/assess/${entryId}`);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {sidePanel === 'users' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <Button variant="secondary" size="sm" disabled={locked} onClick={() => setShowAssignUsersModal(true)}>
                    <FontAwesomeIcon icon={faUserPlus} /> Assign
                  </Button>
                </div>

                {!users.length ? (
                  <EmptyRow text="No users assigned yet." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {users.map(user => (
                      <ListRow key={user.id}>
                        <Avatar label={user.name || user.email} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.name || user.email}
                        </span>
                        {!locked && (
                          <RowRemoveButton
                            onClick={() => unassignUserMutation.mutate(user.id)}
                            label={`Remove ${user.name || user.email}`}
                          />
                        )}
                      </ListRow>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>

        </div>

      </div>

      {showUploadModal && (
        <UploadJsonModal
          onClose={() => setShowUploadModal(false)}
          onUpload={async ({ files, fileTypes }) => {
            try {
              await projectsApi.addResourcesWithFile(id, {
                resources: [],
                apps: [],
                pages: [],
                files,
                fileTypes
              });
              toast.success(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'}`);
              invalidate();
              setShowUploadModal(false);
            } catch (err) {
              toast.error(err.response?.data?.error || err.message || 'Failed to upload files');
            }
          }}
        />
      )}

      {showAddResourcesModal && (
        <AddResourcesModal
          onClose={() => setShowAddResourcesModal(false)}
          existingResourceIds={resources.map(r => r.id)}
          onAdd={async ({ resources: selectedResources, apps: selectedApps, pages: selectedPages }) => {
            try {
              // Send the full { id, name, type } objects (not just IDs) so
              // the project always has a real name/type to display, even
              // if the backend simply stores whatever it's given rather
              // than re-resolving IDs against Domo itself. `apps`/`pages`
              // are the same selection split by type, sent alongside the
              // flat `resources` list.
              await projectsApi.addResources(id, {
                resources: selectedResources,
                apps: selectedApps,
                pages: selectedPages
              });
              toast.success('Resources added');
              invalidate();
              setShowAddResourcesModal(false);
            } catch (err) {
              toast.error(err.response?.data?.error || err.message || 'Failed to add resources');
            }
          }}
        />
      )}

      {showAssignUsersModal && (
        <AssignUsersModal
          onClose={() => setShowAssignUsersModal(false)}
          existingUserIds={users.map(u => u.id)}
          onAssign={async selected => {
            try {
              await projectsApi.assignUsers(id, { userIds: selected });
              toast.success('Users assigned');
              invalidate();
              setShowAssignUsersModal(false);
            } catch (err) {
              toast.error(err.response?.data?.error || err.message || 'Failed to assign users');
            }
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteProjectModal
          projectName={project.name}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => deleteMutation.mutate()}
          deleting={deleteMutation.isLoading}
        />
      )}

    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Delete Project Modal
|--------------------------------------------------------------------------
*/

function DeleteProjectModal({ projectName, onClose, onConfirm, deleting }) {

  return (
    <ModalShell onClose={onClose} label="Delete project" maxWidth={420}>

      <ModalHeader title="Delete Project" onClose={onClose} />

      <div style={{ padding: '20px 22px', fontSize: 13.5, color: 'var(--text-primary)' }}>
        Are you sure you want to delete <strong>{projectName}</strong>? This will
        permanently remove the project, its assigned resources, users, comments,
        and activity history. This action cannot be undone.
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button
          variant="danger"
          loading={deleting}
          onClick={onConfirm}
        >
          <FontAwesomeIcon icon={faTrash} /> Delete Project
        </Button>
      </ModalFooter>

    </ModalShell>
  );
}

/*
|--------------------------------------------------------------------------
| Small presentational helpers
|--------------------------------------------------------------------------
*/

function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'Unknown';

  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr}h ago`;

  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function hero_badge(bg, color) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    background: bg,
    color
  };
}

function HeroButton({ children, onClick, loading, disabled, title, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '9px 16px',
        borderRadius: 'var(--radius-md)',
        border: primary ? 'none' : '1px solid rgba(255,255,255,0.35)',
        background: primary ? '#fff' : 'rgba(255,255,255,0.12)',
        color: primary ? 'var(--accent)' : '#fff',
        fontSize: 13,
        fontWeight: 600,
        cursor: (loading || disabled) ? 'not-allowed' : 'pointer',
        opacity: (loading || disabled) ? 0.6 : 1,
        transition: 'transform .15s, box-shadow .15s',
        whiteSpace: 'nowrap'
      }}
      onMouseOver={e => { if (!loading && !disabled) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseOut={e => { e.currentTarget.style.transform = 'none'; }}
    >
      {loading ? <Spinner size={14} color={primary ? 'var(--accent)' : '#fff'} /> : children}
    </button>
  );
}

function StatChip({ icon, label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'rgba(255,255,255,0.12)',
        borderRadius: 'var(--radius-md)',
        padding: '9px 14px'
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 13, opacity: 0.85 }} />
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 10.5, opacity: 0.8, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

function SectionCard({ icon, iconColor, title, count, action, children }) {
  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '0.5px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        padding: 20
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 'var(--radius-md)',
              background: 'var(--accent-light)',
              color: iconColor || 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {title}
            {count != null && <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}> ({count})</span>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function SidePanelTab({ icon, label, count, active, onClick, indicator }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '12px 8px',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        background: active ? 'var(--accent-light)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        transition: 'background .15s, color .15s',
        position: 'relative'
      }}
      onMouseOver={e => { if (!active) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
      onMouseOut={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 11 }} />
      {label}
      <span
        style={{
          background: active ? 'var(--bg-card)' : 'var(--bg-secondary)',
          color: active ? 'var(--accent)' : 'var(--text-tertiary)',
          borderRadius: 10,
          padding: '1px 6px',
          fontSize: 10,
          fontWeight: 700
        }}
      >
        {count}
      </span>
      {indicator && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--success)',
            position: 'absolute',
            top: 8,
            right: 10
          }}
        />
      )}
    </button>
  );
}

function ListRow({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '9px 11px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-secondary)',
        fontSize: 12.5,
        transition: 'background .15s'
      }}
    >
      {children}
    </div>
  );
}

function RowRemoveButton({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      title="Remove"
      aria-label={label}
      style={{
        border: 'none',
        background: 'transparent',
        color: 'var(--text-tertiary)',
        cursor: 'pointer',
        fontSize: 12,
        padding: 5,
        borderRadius: 'var(--radius-sm)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background .15s, color .15s',
        flexShrink: 0
      }}
      onMouseOver={e => { e.currentTarget.style.background = 'var(--danger-bg)'; e.currentTarget.style.color = 'var(--danger)'; }}
      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
    >
      <FontAwesomeIcon icon={faTrash} />
    </button>
  );
}

function Avatar({ label }) {
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: 'var(--accent)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 600,
        flexShrink: 0
      }}
    >
      {(label || '?').slice(0, 2).toUpperCase()}
    </div>
  );
}

function EmptyRow({ text }) {
  return (
    <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '16px 4px', textAlign: 'center' }}>
      {text}
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Project History Entry
|--------------------------------------------------------------------------
| One row in the project's Assessment History panel. Reuses the same
| status color logic as the Assess page's own History modal so the two
| stay visually consistent.
|--------------------------------------------------------------------------
*/

function ProjectHistoryEntry({ entry, onView }) {

  const status = String(entry?.status || 'completed').toLowerCase();
  const statusColor = status === 'failed' ? 'var(--danger)' : status === 'running' ? 'var(--warning-text)' : 'var(--success)';
  const statusBg = status === 'failed' ? 'var(--danger-bg)' : status === 'running' ? 'var(--warning-bg)' : 'var(--success-bg)';

  const dateStr = entry?.completedAt || entry?.createdAt || entry?.startedAt || entry?.date;
  const formattedDate = (() => {
    if (!dateStr) return 'Unknown date';
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? 'Unknown date' : d.toLocaleString();
  })();

  return (
    <button
      type="button"
      onClick={onView}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        border: '0.5px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '9px 11px',
        background: 'var(--bg-card)',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'background .15s'
      }}
      onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; }}
      onMouseOut={e => { e.currentTarget.style.background = 'var(--bg-card)'; }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
          {formattedDate}
          {entry?.summary?.datasets != null && ` \u00b7 ${entry.summary.datasets} datasets`}
          {entry?.summary?.pages != null && ` \u00b7 ${entry.summary.pages} pages`}
        </div>
      </div>
      <span
        style={{
          background: statusBg,
          color: statusColor,
          borderRadius: 10,
          padding: '2px 8px',
          fontSize: 10,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          textTransform: 'uppercase',
          flexShrink: 0
        }}
      >
        {status}
      </span>
    </button>
  );

}

/*
|--------------------------------------------------------------------------
| Activity Log Entry
|--------------------------------------------------------------------------
| Renders one activity event as a plain-English sentence instead of raw
| action/entityType/details fields, e.g. "Jane Doe added the Page 'Sales
| Overview'" instead of "resource_added / resource / page_456".
|--------------------------------------------------------------------------
*/

const ACTIVITY_ICONS = {
  resource_added:    { icon: '📎', color: 'var(--success)' },
  resource_removed:  { icon: '🗑️', color: 'var(--danger)' },
  user_assigned:     { icon: '👤', color: 'var(--accent)' },
  user_unassigned:   { icon: '👤', color: 'var(--text-tertiary)' },
  comment_added:     { icon: '💬', color: 'var(--accent)' },
  project_locked:    { icon: '🔒', color: 'var(--warning-text)' },
  project_unlocked:  { icon: '🔓', color: 'var(--success)' },
  project_created:   { icon: '✨', color: 'var(--accent)' },
  project_updated:   { icon: '✏️', color: 'var(--text-secondary)' },
  project_merged:    { icon: '🔀', color: 'var(--accent)' }
};

function describeActivity(entry) {

  const who = entry.userName || entry.userEmail || 'Someone';
  const details = entry.details || {};

  switch (entry.action) {

    case 'resource_added':
      return `${who} added the ${details.resourceType || 'resource'} "${details.resourceName || entry.entityId}"`;

    case 'resource_removed':
      return `${who} removed the ${details.resourceType || 'resource'} "${details.resourceName || entry.entityId}"`;

    case 'user_assigned':
      return `${who} assigned ${details.assignedUserName || 'a user'} to this project`;

    case 'user_unassigned':
      return `${who} removed ${details.removedUserName || 'a user'} from this project`;

    case 'comment_added':
      return `${who} commented: "${details.text || ''}"`;

    case 'project_locked':
      return `${who} locked this project`;

    case 'project_unlocked':
      return `${who} unlocked this project`;

    case 'project_created':
      return `${who} created this project`;

    case 'project_updated':
      return `${who} updated the project details`;

    case 'project_merged': {
      const sourceNames = details.sourceProjectNames;
      const namesText = Array.isArray(sourceNames)
        ? sourceNames.join(', ')
        : (sourceNames || '');
      return `${who} merged ${namesText ? `"${namesText}"` : 'other projects'} into this project`;
    }

    default:
      // Fallback for any action types not explicitly handled above —
      // still readable, just less tailored.
      return `${who} performed "${entry.action || 'an action'}"${entry.entityType ? ` on ${entry.entityType}` : ''}`;

  }

}

function ActivityLogEntry({ entry }) {

  const meta = ACTIVITY_ICONS[entry.action] || { icon: '•', color: 'var(--text-secondary)' };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '9px 11px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-secondary)'
      }}
    >
      <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{meta.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>
          {describeActivity(entry)}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 2 }}>
          {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
        </div>
      </div>
    </div>
  );

}

/*
|--------------------------------------------------------------------------
| Add Resources Modal (Apps / Reports)
|--------------------------------------------------------------------------
| Reuses the same GET /domo/apps-pages endpoint the Assess module's "Start
| Assessment" scope picker uses, so a project's resource list is picked
| from the same Apps/Pages tree, keeping both features consistent.
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| Upload JSON Modal
|--------------------------------------------------------------------------
| Lets a user attach one or more JSON files, see them listed, and tag
| each one with what kind of data it holds (App / Page / Card / Dataset /
| Beast Mode / Datasource) before sending them as-is (unparsed) to the
| backend via projectsApi.addResourcesWithFile. The backend reads each
| file according to its tag and matches/merges it into the project.
|--------------------------------------------------------------------------
*/

const UPLOAD_FILE_TYPES = [
  { value: 'app', label: 'App' },
  { value: 'page', label: 'Page' },
  { value: 'card', label: 'Card' },
  { value: 'dataset', label: 'Dataset' },
  { value: 'beastmode', label: 'Beast Mode' },
  { value: 'datasource', label: 'Datasource' }
];

function UploadJsonModal({ onClose, onUpload }) {

  const fileInputRef = useRef(null);
  const [entries, setEntries] = useState([]); // [{ file, type }]
  const [uploading, setUploading] = useState(false);

  const handleBrowseClick = () => fileInputRef.current?.click();

  const handleFilesSelected = (event) => {

    const selected = Array.from(event.target.files || []);
    event.target.value = '';

    if (!selected.length) return;

    const jsonFiles = selected.filter(file => file.type === 'application/json' || file.name.toLowerCase().endsWith('.json'));
    const rejectedCount = selected.length - jsonFiles.length;

    if (rejectedCount > 0) {
      toast.error(`Only .json files are supported — skipped ${rejectedCount} file${rejectedCount === 1 ? '' : 's'}.`);
    }

    if (!jsonFiles.length) return;

    setEntries(prev => [
      ...prev,
      ...jsonFiles.map(file => ({ file, type: '' }))
    ]);

  };

  const handleDrop = (event) => {
    event.preventDefault();
    const dropped = Array.from(event.dataTransfer?.files || []);
    if (!dropped.length) return;

    const jsonFiles = dropped.filter(file => file.type === 'application/json' || file.name.toLowerCase().endsWith('.json'));
    const rejectedCount = dropped.length - jsonFiles.length;

    if (rejectedCount > 0) {
      toast.error(`Only .json files are supported — skipped ${rejectedCount} file${rejectedCount === 1 ? '' : 's'}.`);
    }

    if (jsonFiles.length) {
      setEntries(prev => [...prev, ...jsonFiles.map(file => ({ file, type: '' }))]);
    }
  };

  const setEntryType = (index, type) => {
    setEntries(prev => prev.map((entry, i) => i === index ? { ...entry, type } : entry));
  };

  const removeEntry = (index) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };

  const allTagged = entries.length > 0 && entries.every(entry => entry.type);

  const handleUpload = async () => {
    if (!allTagged) return;
    setUploading(true);
    try {
      await onUpload({
        files: entries.map(entry => entry.file),
        fileTypes: entries.map(entry => entry.type)
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <ModalShell onClose={onClose} label="Upload JSON files" maxWidth={560}>

      <ModalHeader title="Upload JSON Files" onClose={onClose} />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          multiple
          onChange={handleFilesSelected}
          style={{ display: 'none' }}
        />

        <div
          onClick={handleBrowseClick}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          style={{
            border: '1.5px dashed var(--border-med)',
            borderRadius: 'var(--radius-md)',
            padding: '26px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            fontSize: 13,
            background: 'var(--bg-secondary)',
            flexShrink: 0
          }}
        >
          <FontAwesomeIcon icon={faFileArrowUp} style={{ fontSize: 20, color: 'var(--accent)', marginBottom: 8, display: 'block' }} />
          Click to browse, or drag & drop .json files here
        </div>

        {entries.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {entries.map((entry, index) => (
              <div
                key={`${entry.file.name}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 12px',
                  border: '0.5px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-card)'
                }}
              >
                <FontAwesomeIcon icon={faFileArrowUp} style={{ color: 'var(--accent)', flexShrink: 0 }} />

                <span
                  style={{
                    flex: '1 1 auto',
                    fontSize: 12.5,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={entry.file.name}
                >
                  {entry.file.name}
                </span>

                <select
                  value={entry.type}
                  onChange={e => setEntryType(index, e.target.value)}
                  style={{
                    padding: '6px 8px',
                    border: `0.5px solid ${entry.type ? 'var(--border-med)' : 'var(--danger)'}`,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    flexShrink: 0,
                    width: 150
                  }}
                >
                  <option value="">Choose type...</option>
                  {UPLOAD_FILE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => removeEntry(index)}
                  title="Remove file"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0, display: 'flex', flexShrink: 0 }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ))}
          </div>
        )}

        {entries.length > 0 && !allTagged && (
          <div style={{ fontSize: 11.5, color: 'var(--warning-text)' }}>
            Choose a type for every file before uploading.
          </div>
        )}

      </div>

      <ModalFooter>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {entries.length} file{entries.length === 1 ? '' : 's'}
        </span>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!allTagged}
            loading={uploading}
            onClick={handleUpload}
          >
            Upload {entries.length ? `(${entries.length})` : ''}
          </Button>
        </div>
      </ModalFooter>

    </ModalShell>
  );
}

function mergeById(existingList, incomingList, getId) {
  const existingIds = new Set(existingList.map(getId));
  const newOnes = incomingList.filter(item => !existingIds.has(getId(item)));
  return [...existingList, ...newOnes];
}

function AddResourcesModal({ onClose, onAdd, existingResourceIds = [] }) {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apps, setApps] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await assessmentApi.appsPages();
        const payload = resp.data?.data || resp.data || {};
        if (cancelled) return;
        setApps(Array.isArray(payload.apps) ? payload.apps : []);
        setPages(Array.isArray(payload.pages) ? payload.pages : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load apps and pages');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const itemId = item => item?.id ?? item?.appId ?? item?.pageId ?? item?.dataAppId ?? item?.value;
  const itemName = item => item?.name ?? item?.title ?? item?.label ?? item?.pageName ?? item?.appName ?? String(itemId(item));

  const toggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  // Full selected objects (id/name/type), not just IDs. Sending the
  // resolved name/type to the backend means the "Apps & Reports" list on
  // the project page can always render a real name immediately, instead
  // of depending on the backend to resolve the ID back to a name.
  const allItemsById = new Map(
    [...apps, ...pages].map(item => [itemId(item), item])
  );

  const buildSelectedResources = () =>
    selectedIds
      .map(selId => {
        const item = allItemsById.get(selId);
        if (!item) return null;
        const isApp = apps.includes(item);
        return {
          id: selId,
          name: itemName(item),
          type: isApp ? 'App' : 'Page'
        };
      })
      .filter(Boolean);

  // Same selection, split into { apps, pages } arrays (each item still
  // { id, name, type }) — sent alongside the flat `resources` list on Add
  // so the backend can consume whichever shape it needs, without a
  // separate upload route: uploaded and live-picked items both flow
  // through this same split.
  const buildSelectedAppsAndPages = () => {
    const resources = buildSelectedResources();
    return {
      apps: resources.filter(r => r.type === 'App'),
      pages: resources.filter(r => r.type === 'Page')
    };
  };

  const matchesSearch = item => itemName(item).toLowerCase().includes(search.trim().toLowerCase());

  const filteredApps = apps.filter(matchesSearch);
  const filteredPages = pages.filter(matchesSearch);

  const selectableIds = [...filteredApps, ...filteredPages]
    .map(itemId)
    .filter(id => id != null && !existingResourceIds.includes(id));

  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !selectableIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...selectableIds])]);
    }
  };

  const renderList = (list, type) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {list.map((item, index) => {
        const id = itemId(item);
        const key = `${type}-${id ?? index}`;
        const alreadyAdded = existingResourceIds.includes(id);
        const selected = selectedIds.includes(id);
        return (
          <div
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 6px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
              opacity: alreadyAdded ? 0.5 : 1,
              cursor: alreadyAdded ? 'default' : 'pointer',
              transition: 'background .15s',
              background: selected ? 'var(--accent-light)' : 'transparent'
            }}
            onClick={() => !alreadyAdded && id != null && toggle(id)}
          >
            <FontAwesomeIcon icon={faFolder} style={{ color: '#f0b429', fontSize: 13, flexShrink: 0 }} />

            <span
              style={{
                flex: '1 1 auto',
                color: 'var(--accent)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {itemName(item)}
            </span>

            <input
              type="checkbox"
              disabled={alreadyAdded}
              checked={selected || alreadyAdded}
              onChange={() => id != null && toggle(id)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: alreadyAdded ? 'default' : 'pointer', flexShrink: 0 }}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <ModalShell onClose={onClose} label="Add apps and reports" maxWidth={820}>

      <ModalHeader title="Add Apps / Reports" onClose={onClose} />

      <div
        style={{
          padding: '14px 22px',
          borderBottom: '0.5px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}
      >
        <div style={{ flex: 1, position: 'relative' }}>
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: 12 }}
          />
          <input
            type="text"
            placeholder="Filter apps and pages..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 36,
              border: '0.5px solid var(--border-med)',
              borderRadius: 'var(--radius-md)',
              padding: '0 12px 0 32px',
              outline: 'none',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: 13
            }}
          />
        </div>

        <Button
          variant="secondary"
          size="sm"
          disabled={loading || !!error || selectableIds.length === 0}
          onClick={toggleSelectAll}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 22 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}><Spinner /></div>
        ) : error ? (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>
        ) : (!filteredApps.length && !filteredPages.length) ? (
          <EmptyRow text={`No apps or pages match "${search}".`} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
            <div>
              <ModalColumnHeader label={`Apps (${filteredApps.length})`} />
              {renderList(filteredApps, 'app')}
            </div>
            <div>
              <ModalColumnHeader label={`Pages (${filteredPages.length})`} />
              {renderList(filteredPages, 'page')}
            </div>
          </div>
        )}
      </div>

      <ModalFooter>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{selectedIds.length} selected</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!selectedIds.length}
            loading={saving}
            onClick={async () => {
              setSaving(true);
              await onAdd({
                resources: buildSelectedResources(),
                ...buildSelectedAppsAndPages()
              });
              setSaving(false);
            }}
          >
            Add {selectedIds.length ? `(${selectedIds.length})` : ''}
          </Button>
        </div>
      </ModalFooter>

    </ModalShell>
  );
}

/*
|--------------------------------------------------------------------------
| Assign Users Modal
|--------------------------------------------------------------------------
*/

function AssignUsersModal({ onClose, onAssign, existingUserIds = [] }) {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await usersApi.list();
        const payload = resp.data?.data || resp.data || [];
        if (!cancelled) setAllUsers(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredUsers = allUsers.filter(user => {
    const label = `${user.name || ''} ${user.email || ''}`.toLowerCase();
    return label.includes(search.toLowerCase());
  });

  const toggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  return (
    <ModalShell onClose={onClose} label="Assign users" maxWidth={440}>

      <ModalHeader title="Assign Users" onClose={onClose} />

      <div style={{ padding: '14px 22px 0 22px' }}>
        <div style={{ position: 'relative' }}>
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: 12 }}
          />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 36,
              border: '0.5px solid var(--border-med)',
              borderRadius: 'var(--radius-md)',
              padding: '0 12px 0 32px',
              outline: 'none',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: 13
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '14px 22px 22px 22px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}><Spinner /></div>
        ) : error ? (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>
        ) : !filteredUsers.length ? (
          <EmptyRow text="No users found." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredUsers.map(user => {
              const alreadyAssigned = existingUserIds.includes(user.id);
              const selected = selectedIds.includes(user.id);
              return (
                <div
                  key={user.id}
                  onClick={() => !alreadyAssigned && toggle(user.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 8px',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 12.5,
                    cursor: alreadyAssigned ? 'default' : 'pointer',
                    opacity: alreadyAssigned ? 0.5 : 1,
                    background: selected ? 'var(--accent-light)' : 'transparent'
                  }}
                >
                  <Avatar label={user.name || user.email} />
                  <span style={{ flex: 1 }}>{user.name || user.email}</span>
                  <input
                    type="checkbox"
                    disabled={alreadyAssigned}
                    checked={selected || alreadyAssigned}
                    onChange={() => toggle(user.id)}
                    onClick={e => e.stopPropagation()}
                  />
                  {alreadyAssigned && <Badge variant="neutral">Assigned</Badge>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ModalFooter>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{selectedIds.length} selected</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!selectedIds.length}
            loading={saving}
            onClick={async () => {
              setSaving(true);
              await onAssign(selectedIds);
              setSaving(false);
            }}
          >
            Assign {selectedIds.length ? `(${selectedIds.length})` : ''}
          </Button>
        </div>
      </ModalFooter>

    </ModalShell>
  );
}

/*
|--------------------------------------------------------------------------
| Shared Modal Chrome
|--------------------------------------------------------------------------
*/

function ModalShell({ children, onClose, label, maxWidth = 480 }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24
      }}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-lg)',
          maxWidth,
          width: '100%',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div
      style={{
        padding: '18px 22px',
        borderBottom: '0.5px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          border: 'none',
          background: 'transparent',
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          fontSize: 15,
          padding: 4,
          borderRadius: 'var(--radius-sm)'
        }}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}

function ModalFooter({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 22px',
        borderTop: '0.5px solid var(--border)'
      }}
    >
      {children}
    </div>
  );
}

function ModalColumnHeader({ label }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '.03em',
        paddingBottom: 8,
        marginBottom: 6,
        borderBottom: '0.5px solid var(--border)'
      }}
    >
      {label}
    </div>
  );
}
